import classNames from 'classnames';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { useEffect, useRef, useState } from 'react';
import Flickity from 'react-flickity-component';

import { imagePropTypes, linkPropTypes } from '../../../propTypes';
import Icon from '../Atoms/Icon';

import ErrorBoundary from '../Core/ErrorBoundary';
import Listing from '../PackageSearch/Listing';
import { normaliseCarouselItemHeights } from './helpers';

const propTypes = exact({
  totalItemCount: PropTypes.number.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      tournament: PropTypes.shape({ title: PropTypes.string }),
      date: PropTypes.string,
      type: PropTypes.string,
      costPerPerson: PropTypes.number,
      inclusions: PropTypes.arrayOf(PropTypes.string),
      image: imagePropTypes,
      isSoldOut: PropTypes.bool,
      description: PropTypes.string,
      limitedAvailability: PropTypes.bool,
      waitingListLink: linkPropTypes,
      limitedAvailabilityRemainingSpaces: PropTypes.string,
      limitedAvailabilityText: PropTypes.string,
      key: PropTypes.string,
      title: PropTypes.string,
      url: PropTypes.string,
    })
  ).isRequired,
  soldOutText: PropTypes.string.isRequired,
});

const defaultProps = {
  soldOutText: 'SOLD OUT',
};

function PackageCarousel({ soldOutText, items, perPersonText }) {
  const [slidesCount, setSlidesCount] = useState(false);
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const flickityRef = useRef();

  const checkToUpdateUI = () => {
    setSlidesCount(flickityRef.current?.slides.length);
    setSelectedSlideIndex(flickityRef.current.selectedIndex);
    normaliseCarouselItemHeights(flickityRef.current);
    flickityRef.current.resize();
  };

  const flickityRefFunc = (ref) => {
    flickityRef.current = ref;
    flickityRef.current.on('ready', checkToUpdateUI);
    flickityRef.current.on('change', checkToUpdateUI);
  };

  useEffect(() => {
    const handleWindowResize = debounce(checkToUpdateUI, 1000 / 6);
    window.addEventListener('resize', handleWindowResize, { passive: true });
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [checkToUpdateUI]);

  return (
    <ErrorBoundary>
      <div
        className={classNames('FlickityCarousel', {
          'FlickityCarousel-hideDots': slidesCount === 1,
        })}
      >
        <Flickity
          className="FlickityCarousel_flickity"
          flickityRef={flickityRefFunc}
          options={{
            cellAlign: 'left',
            contain: true,
            draggable: true,
            friction: 0.8,
            groupCells: true,
            initialIndex: 0,
            pageDots: true,
            prevNextButtons: false,
            selectedAttraction: 0.2,
          }}
        >
          {items.map(({ id, ...item }) => (
            <div key={item.key} className="FlickityCarousel_cell">
              <Listing
                {...item}
                tourKey={item.key}
                useGridLayout
                soldOutText={soldOutText}
                perPersonText={perPersonText}
              />
            </div>
          ))}
        </Flickity>
        {slidesCount > 1 && (
          <div className="FlickityCarousel_sideButtons">
            <button
              type="button"
              className={`FlickityCarousel_leftButton${
                selectedSlideIndex === 0 ? ' FlickityCarousel_leftButton-disabled' : ''
              }`}
              onClick={() => flickityRef.current?.previous()}
            >
              <Icon id="arrow-left" />
            </button>
            <button
              type="button"
              className={`FlickityCarousel_rightButton${
                selectedSlideIndex + 1 === slidesCount
                  ? ' FlickityCarousel_rightButton-disabled'
                  : ''
              }`}
              onClick={() => flickityRef.current?.next()}
            >
              <Icon id="arrow-right" />
            </button>
          </div>
        )}
        {items.length < 1 && <div className="PackageResults_noResults">No results found</div>}
      </div>
    </ErrorBoundary>
  );
}

PackageCarousel.propTypes = propTypes;
PackageCarousel.defaultProps = defaultProps;

export default PackageCarousel;
